import React from "react";
import { Youtube, Music2, Video, Instagram } from "lucide-react";

/**
 * Affiche un morceau/vidéo hébergé(e) sur une plateforme externe (YouTube, Vimeo, TikTok, etc.)
 * via iframe responsive.
 */
/**
 * Affiche un morceau/vidéo hébergé(e) sur une plateforme externe (YouTube, Vimeo, TikTok, etc.)
 * via iframe responsive. Supporte l'auto-play + auto-advance via postMessage (YouTube, Vimeo).
 *
 * Props:
 *  - track (required)
 *  - onEnded: callback appelé quand la vidéo YouTube/Vimeo se termine (utilisé pour playlist auto-next)
 *  - autoplay: lance la vidéo muette dès l'affichage
 */
export const EmbedPlayer = ({ track, className = "", onEnded, autoplay = false }) => {
  const iframeRef = React.useRef(null);
  const provider = track?.external_provider;
  const embed = track?.external_embed;

  // Enrichit l'URL d'embed avec les params auto-play et enablejsapi pour écouter la fin.
  const embedUrl = React.useMemo(() => {
    if (!embed) return null;
    try {
      const u = new URL(embed);
      if (provider === "youtube") {
        if (autoplay) { u.searchParams.set("autoplay", "1"); u.searchParams.set("mute", "1"); }
        u.searchParams.set("enablejsapi", "1");
        u.searchParams.set("playsinline", "1");
        u.searchParams.set("rel", "0");
      } else if (provider === "vimeo") {
        if (autoplay) { u.searchParams.set("autoplay", "1"); u.searchParams.set("muted", "1"); }
        u.searchParams.set("playsinline", "1");
      }
      return u.toString();
    } catch { return embed; }
  }, [embed, provider, autoplay]);

  // Ecoute postMessage pour détecter la fin YouTube / Vimeo
  React.useEffect(() => {
    if (!onEnded || (provider !== "youtube" && provider !== "vimeo")) return;
    const subscribe = () => {
      const iframe = iframeRef.current;
      if (!iframe?.contentWindow) return;
      if (provider === "youtube") {
        try {
          iframe.contentWindow.postMessage(JSON.stringify({ event: "listening", id: "voamada_yt" }), "*");
          iframe.contentWindow.postMessage(JSON.stringify({ event: "command", func: "addEventListener", args: ["onStateChange"] }), "*");
        } catch { /* ignore */ }
      } else if (provider === "vimeo") {
        try {
          iframe.contentWindow.postMessage(JSON.stringify({ method: "addEventListener", value: "ended" }), "*");
        } catch { /* ignore */ }
      }
    };
    const handleMsg = (e) => {
      try {
        const data = typeof e.data === "string" ? JSON.parse(e.data) : e.data;
        if (!data) return;
        if (provider === "youtube") {
          const info = data.info !== undefined ? data.info : data;
          if (data.event === "onStateChange" && info === 0) onEnded();
          if (data.event === "infoDelivery" && data.info?.playerState === 0) onEnded();
        }
        if (provider === "vimeo" && data.event === "ended") onEnded();
      } catch { /* ignore */ }
    };
    window.addEventListener("message", handleMsg);
    const iframe = iframeRef.current;
    const onLoad = () => { setTimeout(subscribe, 800); };
    iframe?.addEventListener("load", onLoad);
    if (iframe?.contentWindow) setTimeout(subscribe, 1200);
    return () => {
      window.removeEventListener("message", handleMsg);
      iframe?.removeEventListener("load", onLoad);
    };
  }, [onEnded, provider]);

  if (!embed) return null;

  const isAudio = provider === "soundcloud";
  const isPortrait = provider === "tiktok" || provider === "instagram";
  const wrapClass = isAudio
    ? "h-40 w-full"
    : isPortrait
      ? "aspect-[9/16] max-w-[420px] mx-auto"
      : "aspect-video w-full";

  return (
    <div className={`relative ${wrapClass} rounded-2xl overflow-hidden bg-black border border-white/10 ${className}`} data-testid={`embed-${provider}`}>
      <iframe
        ref={iframeRef}
        src={embedUrl}
        title={track.title}
        className="absolute inset-0 w-full h-full"
        frameBorder="0"
        allow="autoplay; encrypted-media; picture-in-picture; fullscreen; accelerometer; gyroscope"
        allowFullScreen
        loading="lazy"
        referrerPolicy="strict-origin-when-cross-origin"
      />
    </div>
  );
};

export const ProviderBadge = ({ provider, className = "" }) => {
  if (!provider) return null;
  const map = {
    youtube:     { color: "#FF0000", label: "YouTube",     Icon: Youtube },
    vimeo:       { color: "#1AB7EA", label: "Vimeo",       Icon: Video },
    tiktok:      { color: "#000000", label: "TikTok",      Icon: Music2 },
    dailymotion: { color: "#0066DC", label: "Dailymotion", Icon: Video },
    soundcloud:  { color: "#FF5500", label: "SoundCloud",  Icon: Music2 },
    instagram:   { color: "#E1306C", label: "Instagram",   Icon: Instagram },
    facebook:    { color: "#1877F2", label: "Facebook",    Icon: Video },
  };
  const info = map[provider];
  if (!info) return null;
  const { Icon } = info;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${className}`}
      style={{ background: `${info.color}20`, color: info.color, border: `1px solid ${info.color}40` }}
      data-testid={`provider-badge-${provider}`}
    >
      <Icon size={10} /> {info.label}
    </span>
  );
};

export default EmbedPlayer;
